// src/Customer/CustomerDashboard/CustomerSettingsAndPreferences.js

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";

import CustomerLayout from "../CustomerLayout";
import { API } from "../../../config";

const STORAGE_KEY = "gocourier_customer_settings_v1";

const defaultSettings = {
  emailUpdates: true,
  smsUpdates: false,
  inAppNotifications: true,
  loadStatusUpdates: true,
  paymentAlerts: true,
  promotions: false,
  usageData: true,
};

const CustomerSettingsAndPreferences = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saved, setSaved] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  
  const token =
  localStorage.getItem("customer_token") ||
  localStorage.getItem("token");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (stored) {
        setSettings({
          ...defaultSettings,
          ...JSON.parse(stored),
        });
      }
    } catch {
      setSettings(defaultSettings);
    }
  }, []);

  const handleToggle = (name) => (event) => {
    setSettings((current) => ({
      ...current,
      [name]: event.target.checked,
    }));
  };

  const handleSaveSettings = () => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(settings)
    );

    setSaved(true);
  };

 const handleRequestAccountDeletion = async () => {
  try {
    setDeleteMessage("");

    const response = await fetch(`${API}/api/submit-ticket`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        subject: "Customer account deletion request",
        message:
          "Customer requested deletion or deactivation of their HaggleFreight account. Support should review legal, tax, payment, safety, fraud prevention, dispute, and compliance retention requirements before taking action.",
        category: "account_deletion",
        priority: "normal",
      }),
    });

    if (!response.ok) {
      throw new Error("Unable to submit account deletion request.");
    }

    setDeleteMessage(
      "Your account deletion request has been submitted. HaggleFreight support will review it according to legal, payment, safety, tax, dispute, and compliance requirements."
    );

    setDeleteDialogOpen(false);
  } catch (error) {
    setDeleteMessage(
      error.message || "Unable to submit account deletion request."
    );

    setDeleteDialogOpen(false);
  }
};

  return (
    <CustomerLayout>
      <Box
        sx={{
          maxWidth: 980,
          mx: "auto",
          py: { xs: 2, md: 4 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.02em",
            mb: 1,
          }}
        >
          Settings & Preferences
        </Typography>

        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "1rem",
            lineHeight: 1.6,
            mb: 4,
            maxWidth: 820,
          }}
        >
          Manage notifications, privacy preferences, and account-level settings.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Notification Settings
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailUpdates}
                    onChange={handleToggle(
                      "emailUpdates"
                    )}
                  />
                }
                label="Email updates"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smsUpdates}
                    onChange={handleToggle(
                      "smsUpdates"
                    )}
                  />
                }
                label="SMS updates"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.inAppNotifications}
                    onChange={handleToggle(
                      "inAppNotifications"
                    )}
                  />
                }
                label="In-app notifications"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.loadStatusUpdates}
                    onChange={handleToggle(
                      "loadStatusUpdates"
                    )}
                  />
                }
                label="Load status updates"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentAlerts}
                    onChange={handleToggle(
                      "paymentAlerts"
                    )}
                  />
                }
                label="Payment alerts"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Privacy Preferences
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.promotions}
                    onChange={handleToggle(
                      "promotions"
                    )}
                  />
                }
                label="Receive promotions and platform updates"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.usageData}
                    onChange={handleToggle(
                      "usageData"
                    )}
                  />
                }
                label="Allow anonymized usage data to improve service"
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              sx={{
                py: 1.3,
                px: 4,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Save Settings
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #fecaca",
              borderRadius: 3,
              p: 3,
              backgroundColor: "#fffafa",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "error.main",
                mb: 1,
              }}
            >
              Delete Account
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              You may request deletion of your HaggleFreight customer account.
              Some records may be retained where required for legal,
              tax, payment, fraud prevention, safety, dispute,
              or compliance purposes.
            </Typography>

            <Button
              variant="outlined"
              color="error"
              onClick={() =>
                setDeleteDialogOpen(true)
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Request Account Deletion
            </Button>

            {deleteMessage && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {deleteMessage}
              </Alert>
            )}
          </Paper>
        </Paper>

        <Snackbar
          open={saved}
          autoHideDuration={3500}
          onClose={() => setSaved(false)}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSaved(false)}
          >
            Settings saved.
          </Alert>
        </Snackbar>

        <Dialog
          open={deleteDialogOpen}
          onClose={() =>
            setDeleteDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Request Account Deletion?
          </DialogTitle>

          <DialogContent>
            <Typography paragraph>
              This will submit a request to delete
              or deactivate your HaggleFreight customer account.
            </Typography>

            <Typography paragraph>
              HaggleFreight may retain certain records
              if required for legal, tax, payment,
              safety, fraud prevention,
              dispute resolution, or compliance purposes.
            </Typography>

            <Typography color="error">
              This action may affect access to loads,
              shipment history, payments,
              messages, and account data.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setDeleteDialogOpen(false)
              }
            >
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={
                handleRequestAccountDeletion
              }
            >
              Submit Deletion Request
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </CustomerLayout>
  );
};

export default CustomerSettingsAndPreferences;